import "./ActivityOverviewPage.css";

import { InteractionObjectType, OrganisationTypeConstants } from "../../../../constants";
import { FileType } from "../../../../models";
import {
  AssetManagerSingleCardWithTabsTemplate,
  DeveloperSingleCardWithTabsTemplate,
  NotesPanel,
} from "../../../../templates";
import { VersionConflictModal } from "../../../../templates/DeveloperProjectCardWithTabsTemplate/components";
import { checkCanManageActivity } from "../../../../utils";
import { Carousel, Column, ProfileMenu, ProfileMenuItem, QuickActionsMenu, Row } from "../../../../widget";
import { DashboardPieCard } from "../../components";
import { useActivityOverviewPage } from "./useActivityOverviewPage";

export const ActivityOverviewPage = (): JSX.Element => {
  const {
    setShowVersionConflictModal,
    setShowQuickActionMenu,
    setShowNotesPanel,
    shouldRefreshActivities,
    handleBackClick,
    isExternalActivity,
    tabsList,
    activityDetails,
    projectDetails,
    discussionsChartData,
    carouselActivity,
    showVersionConflictModal,
    draftActivityHistoryUuid,
    newVersionActivityHistory,
    hasManageProjectActivityPermission,
    hasDiscussionPermission,
    currentUserType,
    backButtonText,
    pageTitle,
    showQuickActionMenu,
    quickActionsMenuOptionsList,
    showNotesPanel,
  } = useActivityOverviewPage();

  const kpis = (): JSX.Element => {
    return (
      <Row>
        <Column span="7_half">
          <Carousel
            activities={carouselActivity ? [carouselActivity] : []}
            status={carouselActivity?.status}
            canManageActivity={checkCanManageActivity(hasManageProjectActivityPermission, carouselActivity?.status)}
            isExternal={isExternalActivity()}
            location="Activity"
            shouldRefreshActivities={shouldRefreshActivities}
          />
        </Column>
        {hasDiscussionPermission && (
          <Column span="4_half">
            <DashboardPieCard
              data={discussionsChartData}
              title="Discussions"
              colours="discussionsKPI"
              noDataMessage="No discussions"
              size={150}
              showValues={false}
            />
          </Column>
        )}
      </Row>
    );
  };

  const quickActions = (): JSX.Element | undefined => {
    return quickActionsMenuOptionsList.length > 0 ? (
      <div className="ActivityOverviewPageQuickActions">
        <QuickActionsMenu onClick={() => setShowQuickActionMenu(true)} />
        <ProfileMenu show={showQuickActionMenu} onClose={() => setShowQuickActionMenu(false)} position={{ top: "0" }}>
          {quickActionsMenuOptionsList.map((el) => (
            <ProfileMenuItem
              key={el.id}
              profileMenuItem={el}
              onClick={() => {
                if (el.action) {
                  el.action();
                }
                setShowQuickActionMenu(false);
              }}
            />
          ))}
        </ProfileMenu>
      </div>
    ) : undefined;
  };

  if (!projectDetails || !activityDetails) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <></>;
  }

  return (
    <>
      {currentUserType === OrganisationTypeConstants.ASSET_MANAGER ? (
        <AssetManagerSingleCardWithTabsTemplate
          activeElement="Portfolio"
          pageTitle={pageTitle}
          titlePill={projectDetails?.standard?.displayName}
          developer={projectDetails?.developer.displayName}
          developerIcon={projectDetails?.developer.files.find((f) => f.type === FileType.SmallLogo)?.file.url}
          tabsList={tabsList}
          carousel={kpis()}
          backButtonText={backButtonText}
          quickActions={quickActions()}
          handleBackClick={handleBackClick}
        />
      ) : (
        DeveloperSingleCardWithTabsTemplate({
          activeElement: "Activities",
          pageTitle,
          titlePill: projectDetails?.standard?.displayName,
          developer:
            currentUserType !== OrganisationTypeConstants.DEVELOPER ? projectDetails?.developer.displayName : undefined,
          developerIcon:
            currentUserType !== OrganisationTypeConstants.DEVELOPER
              ? projectDetails?.developer.files.find((f) => f.type === FileType.SmallLogo)?.file.url
              : undefined,
          tabsList,
          carousel: kpis(),
          backButtonText,
          quickActions: quickActions(),
          handleBackClick,
          currentUserType,
        })
      )}
      <VersionConflictModal
        showVersionConflictModal={showVersionConflictModal}
        draftActivityHistoryUuid={draftActivityHistoryUuid}
        closeModal={() => setShowVersionConflictModal(false)}
        newVersionActivityHistory={newVersionActivityHistory}
      />
      {activityDetails.project?.uuid && currentUserType === OrganisationTypeConstants.DEVELOPMENT_MANAGER && (
        <NotesPanel
          objectUuid={activityDetails.project.uuid}
          objectType={InteractionObjectType.PROJECT}
          show={showNotesPanel}
          onClose={() => setShowNotesPanel(false)}
        />
      )}
    </>
  );
};
